// src/scenes/MainMenuSceneDOM.js - Global DOM implementation (Phaser DOM incompatible)
import BaseMenuScene from "./BaseMenuScene.js";
import { showAuthOverlay } from "../ui/authUI.js";
import { auth } from "../firebaseConfig.js";
import sceneManager from "../utils/SceneManager.js";
import { hasSpecialAccess } from "../services/specialAccessService.js";

export default class MainMenuSceneDOM extends BaseMenuScene {
    constructor() {
        super({ key: "MainMenuSceneDOM" });
        this.overlayId = "main-menu-dom-overlay";
        this.sceneReady = false; // Prevent premature interactions
    }

    create() {
        console.log("MainMenuSceneDOM: create() called - Using global DOM with proper cleanup");
        super.create();
        
        // Set ribbon visibility and hide game container
        this.setRibbonVisibility(false);
        this.hideGameContainer();
        
        console.log("MainMenuSceneDOM: Creating global DOM landing page...");
        
        // Create beautiful DOM landing page using global DOM
        this.createGlobalDOMLandingPage();
        
        console.log("MainMenuSceneDOM: Global DOM interface created");
    }

    hideGameContainer() {
        const gameContainer = document.getElementById("game-container");
        if (gameContainer) {
            this.originalGameContainerStyles = {
                display: gameContainer.style.display || 'block',
                visibility: gameContainer.style.visibility || 'visible'
            };
            gameContainer.style.display = 'none';
            gameContainer.style.visibility = 'hidden';
        }
    }

    showGameContainer() {
        const gameContainer = document.getElementById("game-container");
        if (gameContainer) {
            // CRITICAL: Restore PPT Hero expected positioning (from audit)
            gameContainer.style.visibility = 'visible';
            gameContainer.style.display = 'block';
            gameContainer.style.top = '50px';
            gameContainer.style.height = 'calc(100% - 50px)';
            gameContainer.style.opacity = '1';
            gameContainer.style.filter = 'none';
            
            // Also ensure top ribbon is visible (PPT Hero dependency)
            const topRibbon = document.getElementById("top-ribbon");
            if (topRibbon) {
                topRibbon.style.display = 'flex';
            }
            
            // CRITICAL: Canvas styling for Rex UI (from PPT Hero requirements)
            const canvas = document.querySelector('canvas');
            if (canvas) {
                canvas.style.opacity = '1';
                canvas.style.filter = 'none';
                canvas.style.touchAction = "pan-y"; // FIXED: Allow mobile scrolling
            }
            
            // CRITICAL: Call CanvasFixer (PPT Hero dependency)
            try {
                if (window.CanvasFixer && typeof window.CanvasFixer.fixAndFocusCanvas === 'function') {
                    window.CanvasFixer.fixAndFocusCanvas();
                }
            } catch (e) {
                console.warn("Error calling CanvasFixer:", e);
            }
            
            // CRITICAL: Clean up any DOM-introduced CSS pollution  
            this.cleanupCSSPollution();
            
            // CRITICAL: Force scene to top for Rex UI visibility
            if (window.gameInstance) {
                const currentScene = window.gameInstance.scene.getScene('PPTHeroGenericScene') || 
                                   window.gameInstance.scene.getScene('PPTHeroBainScene');
                if (currentScene && currentScene.scene && typeof currentScene.scene.bringToTop === 'function') {
                    currentScene.scene.bringToTop();
                    console.log('MainMenuSceneDOM: Brought PPT Hero scene to top for Rex UI visibility');
                }
            }
            
            console.log('MainMenuSceneDOM: Complete Rex UI environment restored with CSS cleanup');
        }
    }

    createGlobalDOMLandingPage() {
        // Remove existing overlay
        this.removeExistingOverlay();

        // Create main overlay
        const overlay = document.createElement('div');
        overlay.id = this.overlayId;
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
            z-index: 10000;
            overflow-y: auto;
            font-family: 'Segoe UI', sans-serif;
            color: white;
        `;

        overlay.innerHTML = `
            <div style="display: flex; justify-content: space-between; align-items: center; padding: 24px 48px; background: rgba(255, 255, 255, 0.02); border-bottom: 1px solid rgba(255, 255, 255, 0.1);">
              <div style="font-size: 28px; font-weight: 800; color: #00d4ff;">DeckOps.ai</div>
              <div style="display: flex; gap: 16px;">
                <button data-action="sign-in" style="padding: 12px 24px; border: 2px solid #00d4ff; border-radius: 8px; background: transparent; color: #00d4ff; font-weight: 600; cursor: pointer;">Sign In</button>
                <button data-action="sign-up" style="padding: 12px 24px; border: none; border-radius: 8px; background: #00d4ff; color: white; font-weight: 600; cursor: pointer;">Get Started</button>
              </div>
            </div>

            <div style="text-align: center; padding: 80px 48px; max-width: 1200px; margin: 0 auto;">
              <h1 style="font-size: 56px; font-weight: 800; margin-bottom: 24px; color: white;">Executive Performance Intelligence</h1>
              <p style="font-size: 22px; color: rgba(255, 255, 255, 0.8); margin-bottom: 48px; line-height: 1.5;">Master the digital tools that drive executive excellence. From PowerPoint mastery to strategic thinking, elevate your professional capabilities.</p>
              
              <div style="display: flex; gap: 24px; justify-content: center; margin-bottom: 80px;">
                <button data-action="start-trial" style="padding: 18px 36px; border-radius: 12px; font-size: 16px; font-weight: 600; cursor: pointer; border: none; background: #00d4ff; color: white;">Start Free Trial</button>
                <button data-action="explore-games" style="padding: 18px 36px; border-radius: 12px; font-size: 16px; font-weight: 600; cursor: pointer; background: transparent; color: white; border: 2px solid rgba(255, 255, 255, 0.2);">Explore Platform</button>
              </div>
            </div>

            <div style="padding: 0 48px 80px 48px; max-width: 1400px; margin: 0 auto;">
              <h2 style="text-align: center; font-size: 36px; font-weight: 700; margin-bottom: 16px; color: white;">Performance Modules</h2>
              <p style="text-align: center; font-size: 18px; color: rgba(255, 255, 255, 0.7); margin-bottom: 48px;">Choose your path to executive excellence</p>

              <div style="background: rgba(168, 85, 247, 0.1); border: 1px solid rgba(168, 85, 247, 0.2); border-radius: 12px; padding: 20px; margin-bottom: 32px; text-align: center;">
                <h3 style="color: #a855f7; font-size: 18px; font-weight: 600; margin-bottom: 8px;">🎯 Limited Preview Access</h3>
                <p style="color: rgba(255, 255, 255, 0.8); font-size: 14px;">Experience select training modules. Sign up for full analytics, progress tracking, competitive leaderboards, and firm-specific content.</p>
              </div>

              <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 24px;">
                <div data-game="ppt-hero" style="background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 16px; padding: 28px; cursor: pointer; transition: all 0.3s ease;">
                  <div style="font-size: 48px; margin-bottom: 16px;">📊</div>
                  <h3 style="font-size: 24px; font-weight: 700; margin-bottom: 12px; color: white;">PPT Hero</h3>
                  <p style="font-size: 14px; color: rgba(255, 255, 255, 0.8); margin-bottom: 20px;">Master PowerPoint shortcuts and presentation workflows.</p>
                  <button style="width: 100%; padding: 14px; border: none; border-radius: 10px; background: #00d4ff; color: white; cursor: pointer;">Try Limited Version</button>
                </div>
                
                <div data-game="classify-tech" style="background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 16px; padding: 28px; cursor: pointer; transition: all 0.3s ease;">
                  <div style="font-size: 48px; margin-bottom: 16px;">🔬</div>
                  <h3 style="font-size: 24px; font-weight: 700; margin-bottom: 12px; color: white;">Classify Tech</h3>
                  <p style="font-size: 14px; color: rgba(255, 255, 255, 0.8); margin-bottom: 20px;">Technology categorization and emerging tech identification.</p>
                  <button style="width: 100%; padding: 14px; border: none; border-radius: 10px; background: #00d4ff; color: white; cursor: pointer;">Play Now</button>
                </div>
              </div>

              <div style="text-align: center; margin-top: 64px; padding: 32px; background: rgba(255, 255, 255, 0.02); border-radius: 16px; border: 1px solid rgba(255, 255, 255, 0.1);">
                <h3 style="font-size: 24px; font-weight: 600; margin-bottom: 16px; color: white;">Ready to Master Executive Excellence?</h3>
                <p style="font-size: 16px; color: rgba(255, 255, 255, 0.8); margin-bottom: 24px;">Join thousands of executives developing world-class digital proficiency</p>
                <button data-action="get-full-access" style="padding: 20px 40px; border: none; border-radius: 12px; background: #00d4ff; color: white; font-size: 18px; font-weight: 600; cursor: pointer;">Get Full Access</button>
              </div>
            </div>
        `;

        document.body.appendChild(overlay);

        // CRITICAL FIX: Delay event listeners to prevent auto-clicking during creation
        setTimeout(() => {
            this.addGlobalDOMEventListeners();
            this.sceneReady = true;
            console.log("MainMenuSceneDOM: Event listeners activated - scene ready for user interaction");
        }, 1000); // Sufficient delay to prevent auto-clicking
        
        // CRITICAL: Initialize camera for SceneManager compatibility
        setTimeout(() => {
            if (this.cameras && this.cameras.main) {
                this.cameras.main.setVisible(false); // Hide camera since we're using DOM
                console.log("MainMenuSceneDOM: Camera initialized and hidden for SceneManager compatibility");
            }
        }, 100);
    }

    addGlobalDOMEventListeners() {
        // Add click listeners using standard DOM events
        const actionButtons = document.querySelectorAll('[data-action]');
        actionButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                const action = e.currentTarget.dataset.action;
                this.handleAction(action);
            });
        });

        const gameCards = document.querySelectorAll('[data-game]');
        gameCards.forEach(card => {
            card.addEventListener('click', (e) => {
                const game = e.currentTarget.dataset.game;
                this.handleGameAction(game);
            });
        });
        
        console.log("MainMenuSceneDOM: Global DOM event listeners added");
    }

    handleAction(action) {
        // CRITICAL: Prevent interactions during scene creation
        if (!this.sceneReady) {
            console.log('MainMenuSceneDOM: Ignoring premature action during creation:', action);
            return;
        }
        
        console.log(`MainMenuSceneDOM: Handling action: ${action}`);
        
        switch (action) {
            case 'sign-in':
                this.showSignIn();
                break;
            case 'sign-up':
            case 'start-trial':
            case 'get-full-access':
                this.showSignUp();
                break;
            case 'explore-games':
                // Could scroll to games section
                break;
            default:
                console.log('Unknown action:', action);
        }
    }

    handleGameAction(game) {
        // CRITICAL: Prevent interactions during scene creation
        if (!this.sceneReady) {
            console.log('MainMenuSceneDOM: Ignoring premature game action during creation:', game);
            return;
        }
        
        console.log(`MainMenuSceneDOM: Handling game action for: ${game}`);

        switch (game) {
            case 'ppt-hero':
                this.startPPTHero();
                break;
            case 'classify-tech':
                this.startClassifyTech();
                break;
            default:
                console.log('Unknown game:', game);
        }
    }

    // Action methods
    async showSignIn() {
        console.log("MainMenuSceneDOM: Opening sign in dialog");
        try {
            // ENTERPRISE REFACTOR: Use unified authentication service
            if (window.enterpriseAuthService) {
                console.log("🏢 Using EnterpriseAuthService for sign in");
                await window.enterpriseAuthService.showAuthentication('signin');
            } else {
                console.warn("🏢 EnterpriseAuthService not available, using fallback");
                // Fallback to basic auth overlay
                showAuthOverlay();
                setTimeout(() => {
                    const emailInput = document.getElementById('auth-email');
                    if (emailInput) emailInput.focus();
                }, 100);
            }
        } catch (e) {
            console.error("Error showing auth overlay:", e);
            this.showMessage("Error showing login form", 2000, true);
        }
    }

    async showSignUp() {
        console.log("MainMenuSceneDOM: Opening sign up dialog");
        try {
            // ENTERPRISE REFACTOR: Use unified authentication service
            if (window.enterpriseAuthService) {
                console.log("🏢 Using EnterpriseAuthService for sign up");
                await window.enterpriseAuthService.showAuthentication('signup');
            } else {
                console.warn("🏢 EnterpriseAuthService not available, using fallback");
                // Fallback to basic auth overlay
                showAuthOverlay();
                setTimeout(() => {
                    const signupLink = document.getElementById('switch-to-signup-link');
                    if (signupLink) signupLink.click();
                }, 100);
            }
        } catch (e) {
            console.error("Error showing auth overlay:", e);
            this.showMessage("Error showing signup form", 2000, true);
        }
    }

    // Game navigation methods - LET PPT Hero handle its own environment
    async startPPTHero() {
        console.log("MainMenuSceneDOM: Starting PPT Hero");
        
        // CRITICAL: ONLY remove DOM overlay - let PPT Hero handle container setup
        this.removeExistingOverlay();
        
        // Use EXACT same method as working old MainMenuScene
        import('./ppt-hero/PPTHeroSceneManager.js').then(module => {
            const PPTHeroSceneManager = module.default;
            PPTHeroSceneManager.startPPTHero(this, 'generic');
        }).catch(error => {
            console.error('Error loading PPT Hero:', error);
            this.showMessage('Error loading PPT Hero. Please try again.', 3000, true);
        });
    }

    startClassifyTech() {
        console.log("MainMenuSceneDOM: Starting Classify Tech");
        // CRITICAL: ONLY remove DOM overlay - let game handle container setup
        this.removeExistingOverlay();
        sceneManager.switchToScene("ClassifyScene");
    }

    showMessage(text, duration = 2000, isError = false) {
        if (super.showMessage) {
            super.showMessage(text, duration, isError);
        } else {
            console.log(`MainMenu Message: ${text}`);
        }
    }

    removeExistingOverlay() {
        const existing = document.getElementById(this.overlayId);
        if (existing) {
            existing.remove();
        }
    }

    cleanupCSSPollution() {
        // CRITICAL: Remove ALL DOM-introduced styles that might interfere with Rex UI
        const styleElements = document.querySelectorAll('style');
        let removedCount = 0;
        
        styleElements.forEach(style => {
            const content = style.textContent || '';
            // Remove styles that might interfere with Phaser/Rex UI
            if (content.includes('@keyframes') || 
                content.includes('position: fixed') || 
                content.includes('z-index:') ||
                content.includes('.header') ||
                content.includes('.hero-section') ||
                content.includes('.games-grid')) {
                style.remove();
                removedCount++;
            }
        });
        
        console.log(`MainMenuSceneDOM: Cleaned up ${removedCount} DOM style elements that could interfere with Rex UI`);
        
        // Force complete style reset for Rex UI compatibility
        const canvas = document.querySelector('canvas');
        if (canvas) {
            canvas.style.position = '';
            canvas.style.zIndex = '';
            canvas.style.transform = '';
        }
    }

    shutdown() {
        console.log("MainMenuSceneDOM shutting down.");
        this.removeExistingOverlay();
        // Let games handle their own container environment
    }

    destroy() {
        this.removeExistingOverlay();
        // Let games handle their own container environment
        super.destroy();
    }
}
